/**
 * NamedDefaultPopovers — Designer-side popover wrappers for the add + drop
 * named-DEFAULT forms. mssql-only — non-mssql engines render disabled
 * triggers pointing at the column-form path. Mirrors `CheckPopovers` from
 * Slice B4.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B5.
 */
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import {
  getAddNamedDefaultUnsupportedTooltip,
  isAddNamedDefaultSupported,
} from '../../edit/edits/addNamedDefault';
import {
  getDropNamedDefaultUnsupportedTooltip,
  isDropNamedDefaultSupported,
} from '../../edit/edits/dropNamedDefault';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { DisabledAffordance } from './DisabledAffordance';
import { NamedDefaultAddFormBody } from './NamedDefaultAddFormBody';
import { NamedDefaultDropFormBody } from './NamedDefaultDropFormBody';

export interface NamedDefaultAddPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function NamedDefaultAddPopover(props: NamedDefaultAddPopoverProps) {
  const { editContext, testId = 'named-default-add-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isAddNamedDefaultSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getAddNamedDefaultUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Plus className="h-3.5 w-3.5" />
        Named default
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Add named default"
        >
          <Plus className="h-3.5 w-3.5" />
          Named default
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <NamedDefaultAddFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export interface NamedDefaultDropPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function NamedDefaultDropPopover(props: NamedDefaultDropPopoverProps) {
  const { editContext, testId = 'named-default-drop-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isDropNamedDefaultSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getDropNamedDefaultUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Drop named default
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Drop named default"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Drop named default
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="w-80"
        data-testid={testId}
      >
        {open ? (
          <NamedDefaultDropFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
